import { Card, Grid, Icon, InputLabel, Menu, MenuItem, Select } from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import DataTable from "components/Tables/DataTable";
import dataMateriaPrima from "./dataMateriaPrima";
import SoftBox from "components/SoftBox";
import { getColor } from "utils/colors";
import SinDatos from "components/Carteles/SinDatos";
import Separador from "../Separador";
import ModalAnteriores from "../ModalAnteriores";
import UNIDADES from "../unidades";

const MateriaPrima = ({
  idDeclaracion,
  listadoMateriaPrima,
  setListadoMateriaPrima,
  finalizada,
  idEmpresa,
}) => {
  const [materiaPrima, setMateriaPrima] = useState([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMateriaPrima, setSelectedMateriaPrima] = useState(null);
  const [modalAnteriores, setModalAnteriores] = useState(false);

  const { columns, getRows } = dataMateriaPrima;

  const handleOpenMenu = useCallback((event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedMateriaPrima(item);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenMenu(false);
    setSelectedMateriaPrima(null);
  }, []);

  const handleChangeMateriaPrima = (e) => {
    const { name, value } = e.target;
    setMateriaPrima((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddMateriaPrima = (materiaPrima) => {
    setListadoMateriaPrima((prev) => [...prev, materiaPrima]);
    setMateriaPrima([]);
  };

  const memoizedRows = useMemo(() => {
    if (listadoMateriaPrima) {
      const listadoSinBorrar = listadoMateriaPrima.filter((item) => !item.borrado);
      return getRows(listadoSinBorrar, handleOpenMenu);
    }
    return [];
  }, [listadoMateriaPrima, handleOpenMenu]);

  // Render del menú contextual
  const renderMenu = !finalizada && (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          handleCloseMenu();
          setListadoMateriaPrima((prev) => {
            if (!selectedMateriaPrima.id) {
              return prev.filter(
                (item) => item.compuestoQuimico !== selectedMateriaPrima.compuestoQuimico
              );
            } else {
              return prev.map((item) => {
                if (item.id === selectedMateriaPrima.id) {
                  return { ...item, borrado: true };
                }
                return item;
              });
            }
          });
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Quitar</SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            delete
          </Icon>
        </SoftBox>
      </MenuItem>
    </Menu>
  );

  return (
    <Grid container p={2}>
      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="flex-end">
        <SoftTypography variant="h4" fontWeight="bold">
          Materias Primas
        </SoftTypography>
        {!finalizada && (
          <SoftButton
            variant="outlined"
            color="dark"
            sx={{
              cursor: "pointer",
              p: 1,
            }}
            onClick={() => setModalAnteriores(true)}
            disabled={finalizada}
          >
            CARGAR ANTERIORES
            <Icon fontSize="small" sx={{ verticalAlign: "middle", color: "gobSecondary", ml: 0.5 }}>
              cached
            </Icon>
          </SoftButton>
        )}
      </Grid>
      {!finalizada && (
        <Grid item xs={12}>
          <Grid container p={2} spacing={1}>
            <Grid item xs={6} md={4}>
              <InputLabel htmlFor="compuestoQuimico">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Compuesto Químico
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="compuestoQuimico"
                placeholder="Compuesto Químico"
                value={materiaPrima.compuestoQuimico || ""}
                fullWidth
                name="compuestoQuimico"
                onChange={handleChangeMateriaPrima}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <InputLabel htmlFor="unidad">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Unidad
                </SoftTypography>
              </InputLabel>
              <Select
                id="unidad"
                value={materiaPrima.unidad || ""}
                fullWidth
                name="unidad"
                onChange={handleChangeMateriaPrima}
                defaultValue=""
                displayEmpty
                sx={{
                  fontStyle: materiaPrima.unidad ? "normal" : "italic",
                }}
              >
                <MenuItem value="" disabled>
                  Seleccione una unidad
                </MenuItem>
                {UNIDADES.map((unidad) => (
                  <MenuItem key={unidad} value={unidad}>
                    {unidad}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} md={2}>
              <InputLabel htmlFor="estado">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Estado
                </SoftTypography>
              </InputLabel>
              <Select
                id="estado"
                value={materiaPrima.estado || ""}
                fullWidth
                name="estado"
                onChange={handleChangeMateriaPrima}
                defaultValue=""
                displayEmpty
                sx={{
                  fontStyle: materiaPrima.estado ? "normal" : "italic",
                  color: materiaPrima.estado ? "black" : "red",
                }}
              >
                <MenuItem value="" disabled>
                  Seleccione un estado
                </MenuItem>
                <MenuItem value="Líquido">Líquido</MenuItem>
                <MenuItem value="Semi-Sólido">Semi-Sólido</MenuItem>
                <MenuItem value="Sólido">Sólido</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} md={2}>
              <InputLabel htmlFor="cantidadAnual">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Cantidad Anual
                </SoftTypography>
              </InputLabel>
              <SoftInputNumber
                id="cantidadAnual"
                placeholder="Cantidad Anual"
                value={materiaPrima.cantidadAnual || ""}
                fullWidth
                onChange={handleChangeMateriaPrima}
                name="cantidadAnual"
              />
            </Grid>
            <Grid item xs={12} md={2} display={"flex"} alignItems="flex-end">
              <SoftButton
                variant="gradient"
                color="primary"
                fullWidth
                onClick={() => {
                  handleAddMateriaPrima(materiaPrima);
                  setMateriaPrima([]);
                }}
                disabled={
                  !materiaPrima.compuestoQuimico ||
                  !materiaPrima.unidad ||
                  !materiaPrima.estado ||
                  !materiaPrima.cantidadAnual
                }
              >
                Agregar +
              </SoftButton>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!finalizada && <Separador />}
      <Grid item xs={12} py={1}>
        {listadoMateriaPrima?.length > 0 ? (
          <DataTable columns={columns} rows={memoizedRows} />
        ) : (
          <SinDatos textoTitulo="No hay materias primas cargadas" />
        )}
        {renderMenu}
      </Grid>
      <ModalAnteriores
        open={modalAnteriores}
        handleClose={() => setModalAnteriores(false)}
        idEmpresa={idEmpresa}
        setListado={setListadoMateriaPrima}
        tipo="materia prima"
      />
    </Grid>
  );
};

export default memo(MateriaPrima);

MateriaPrima.propTypes = {
  idDeclaracion: PropTypes.string.isRequired,
  listadoMateriaPrima: PropTypes.array.isRequired,
  setListadoMateriaPrima: PropTypes.func.isRequired,
  finalizada: PropTypes.bool,
  idEmpresa: PropTypes.string,
};
